import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ExternalLink, Pencil } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useState } from 'react'

import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Button } from '@/components/elements/button'
import { getOrganizations } from '@/services/organization.service'
import { updateUser } from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'

import AddEditOrganizationSlideout from './AddEditOrganizationSlideout'

const Organizations = observer(() => {
  const { isXverseAdmin, user, setUser } = useContext(UserStoreContext)

  const [sorting, setSorting] = useState([{ id: 'name', desc: false }])
  const [pagination, setPagination] = useState({ pageSize: 20, pageIndex: 0 })
  const [showSlideout, setShowSlideout] = useState(false)
  const [editOrganization, setEditOrganization] = useState(null)

  const { data: organizations, isPending } = useQuery({
    queryKey: ['organizations', sorting, pagination],
    queryFn: () => getOrganizations({ sorting, pagination }),
    placeholderData: keepPreviousData,
  })

  const openEdit = (organization) => {
    setEditOrganization(organization)
    setShowSlideout(true)
  }

  // Switch context into the org. PrivateRoute's reaction on activeOrganization
  // navigates to the org dashboard.
  const openOrganization = async (organization) => {
    const response = await updateUser({
      id: user.id,
      activeOrganization: organization.id,
      activeEntity: null,
    })
    setUser(response)
  }

  return (
    <>
      <div className="mb-4 flex justify-end">
        <Button disabled={!isXverseAdmin} onClick={() => setShowSlideout(true)}>
          Add Organization
        </Button>
      </div>

      <DataTable
        columns={[
          {
            accessorKey: 'name',
            header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
            cell: ({ row }) => (
              <button
                type="button"
                className="text-action cursor-pointer text-left hover:underline"
                disabled={!isXverseAdmin}
                onClick={() => openEdit(row.original)}
              >
                {row.original.name}
              </button>
            ),
          },
          {
            accessorKey: 'email',
            header: 'Email',
            cell: ({ row }) => row.original.email || '—',
          },
          {
            id: 'open',
            cell: ({ row }) => (
              <Button
                className="place-self-end"
                variant="outline"
                size="sm"
                onClick={() => openOrganization(row.original)}
              >
                <ExternalLink className="mr-1 h-4 w-4" />
                Open
              </Button>
            ),
            size: 80,
          },
          {
            id: 'edit',
            cell: ({ row }) => (
              <Button
                className="place-self-end"
                disabled={!isXverseAdmin}
                variant="outline"
                size="sm"
                onClick={() => openEdit(row.original)}
              >
                <Pencil className="mr-1 h-4 w-4" />
                Manage
              </Button>
            ),
            size: 80,
          },
        ]}
        data={organizations?.results || []}
        loading={isPending}
        pagination={pagination}
        rowCount={organizations?.count || 0}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
      />

      {showSlideout && (
        <AddEditOrganizationSlideout
          editOrganization={editOrganization}
          resetAndClose={() => {
            setShowSlideout(false)
            setEditOrganization(null)
          }}
        />
      )}
    </>
  )
})

export default Organizations
